import { useEffect, useState } from 'react'
import ClearIcon from '@mui/icons-material/Clear'
import PlanChangePaymentForm from '../../../../payment/PlanChangePaymentForm'
import CustomButton from '../../../../ui/CustomButton'
import { Modal } from '../../../../ui/Modal'
import PaymentCardSelector from './PaymentCardSelector'

const AddonConfirmContent = ({
  selectedAddonsSummary,
  totalUf,
  totalClp,
  ufValue,
  paymentStep,
  onPaymentSuccess,
  onCancel,
  isPending,
  customerId,
  cardsIds,
  selectedCard,
  cards = [],
  cardsLoading = false,
  selectedCardId = '',
  onSelectCard,
  onOpenAddCard,
  errorMessage,
  priorStepManualReviewNotice = false,
  paymentTypeId = null
}) => {
  const [showFinalConfirmationModal, setShowFinalConfirmationModal] = useState(false)
  const isFinalConfirmationStep = paymentStep === 2
  const finalSubmitLabel = 'Confirmar y pagar'
  const finalConfirmationFormId = 'addon-final-confirmation-form'

  useEffect(() => {
    setShowFinalConfirmationModal(isFinalConfirmationStep)
  }, [isFinalConfirmationStep])

  const paymentForm = (
    <PlanChangePaymentForm
      key={`${paymentStep}-${selectedCard?.id ?? 'none'}-${paymentTypeId ?? 'all'}`}
      amount={totalClp > 0 ? totalClp : 1}
      onSuccess={onPaymentSuccess}
      customerId={customerId}
      cardsIds={cardsIds}
      showSavedCard={isFinalConfirmationStep}
      savedCardCvvOnly={isFinalConfirmationStep}
      formId={isFinalConfirmationStep ? finalConfirmationFormId : undefined}
      hideSubmitButton={isFinalConfirmationStep}
      paymentTypeId={paymentTypeId}
      brickId={paymentStep === 1 ? 'addonContract' : 'addonContractConfirm'}
      submitLabel={isFinalConfirmationStep ? finalSubmitLabel : undefined}
      successMessage='Addons contratados correctamente.'
      securityMessage={
        isFinalConfirmationStep
          ? undefined
          : 'Por seguridad, te pedimos confirmar nuevamente los datos de tu tarjeta para completar el pago.'
      }
    />
  )

  return (
    <div className='relative space-y-4'>
      <Modal
        isOpen={showFinalConfirmationModal && isFinalConfirmationStep}
        onClose={() => setShowFinalConfirmationModal(false)}
        closeOnBackdrop={false}
      >
        <div className='flex max-h-[92vh] w-[min(44rem,calc(100vw-2rem))] flex-col overflow-hidden rounded-[28px] bg-white shadow-2xl'>
          <div className='flex items-center justify-between bg-primary px-4 py-2 text-white sm:px-8'>
            <div />

            <button
              type='button'
              onClick={() => setShowFinalConfirmationModal(false)}
              className='modal-control text-white hover:bg-white/20 rounded p-0.5 transition-colors'
              aria-label='Cerrar confirmacion final'
            >
              <ClearIcon style={{ fontSize: 20 }} />
            </button>
          </div>

          <div className='flex-1 space-y-5 overflow-y-auto px-6 py-6 sm:px-8'>
            {selectedAddonsSummary?.length > 0 && (
              <div className='rounded-[24px] border border-slate-200 bg-white p-6 shadow-sm'>
                <div className='mb-4 flex items-center justify-between'>
                  <div>
                    <p className='text-[14px] font-sans font-semibold uppercase tracking-[0.18em] text-primary/80'>
                      Resumen
                    </p>
                    <h4 className='mt-2 text-xl font-sans font-bold text-slate-800'>
                      Add-ons seleccionados
                    </h4>
                  </div>
                  <span className='text-xs font-sans text-slate-400'>
                    {selectedAddonsSummary.length} items
                  </span>
                </div>

                <div className='divide-y divide-gray-100'>
                  {selectedAddonsSummary.map(item => (
                    <div
                      key={item.addonId}
                      className='flex items-center justify-between py-3'
                    >
                      <span className='truncate text-sm font-sans font-medium text-gray-800'>{item.name}</span>
                      <div className='flex h-9 w-9 items-center justify-center rounded-full bg-accent2 text-sm font-sans font-semibold text-white'>
                        {item.quantity}
                      </div>
                    </div>
                  ))}
                </div>

                <div className='mt-5 rounded-[20px] border border-accent2 bg-accent2/5 px-4 py-4'>
                  <div className='flex justify-between gap-4'>
                    <span className='text-sm text-slate-600'>Total</span>
                    <div className='text-right'>
                      <p className='text-xl font-sans font-semibold text-slate-900'>
                        {(Number(totalUf) || 0).toFixed(2)} UF + IVA
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {selectedCard ? (
              <div className='rounded-[24px] border border-slate-200 bg-white p-6 shadow-sm'>
                <div>
                  <p className='mt-2 text-sm font-sans leading-6 text-slate-600'>
                    Esta sera la tarjeta con la que se cobrara y validara tu contratacion de add-ons.
                  </p>
                </div>

                <div className='mt-4 pointer-events-none'>
                  <PaymentCardSelector
                    cards={[selectedCard]}
                    selectedCardId={String(selectedCard.id)}
                  />
                </div>
              </div>
            ) : (
              <div className='rounded-[24px] border border-amber-200 bg-amber-50 p-4 text-sm font-sans text-amber-800 shadow-sm'>
                No se encontro la tarjeta seleccionada. Vuelve al paso anterior y elige una tarjeta
                antes de confirmar.
              </div>
            )}

            <div className='rounded-[24px] border border-slate-200 bg-white p-6 shadow-sm'>
              <div className='mb-4'>
                <p className='text-[14px] font-sans font-semibold uppercase tracking-[0.18em] text-primary/80'>
                  Ingresa tu CVV para confirmar
                </p>
                <p className='mt-2 text-sm font-sans leading-6 text-slate-600'>
                  Por seguridad, te pedimos este codigo para confirmar la transaccion. Tus datos
                  estan protegidos y se procesaran de forma segura.
                </p>
              </div>

              {priorStepManualReviewNotice && (
                <div className='mb-4 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-sans text-amber-900'>
                  El pago del paso anterior quedo en revision. Completa la confirmacion para que los
                  add-ons queden pendientes de activacion hasta validarse.
                </div>
              )}

              {errorMessage && (
                <div className='mb-4 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-sans text-red-700'>
                  {errorMessage}
                </div>
              )}

              {paymentForm}
            </div>
          </div>

          <div className='flex justify-end gap-3 border-t border-slate-200 bg-slate-50 px-6 py-4 sm:px-8'>
            <CustomButton
              title='Volver'
              color='primary'
              className='rounded-full px-5 py-2'
              onClick={() => setShowFinalConfirmationModal(false)}
            />
            <CustomButton
              type='submit'
              form={finalConfirmationFormId}
              title={finalSubmitLabel}
              color='accent2'
              className='rounded-full px-5 py-2'
              disabled={isPending}
            />
          </div>
        </div>
      </Modal>

      {isPending && (
        <div className='absolute inset-0 z-50 flex flex-col items-center justify-center rounded-lg bg-white/90'>
          <div className='mb-4 h-16 w-16 animate-spin rounded-full border-b-4 border-t-4 border-accent1' />
          <h3 className='mb-2 text-xl font-bold text-gray-900'>Procesando contratacion de addons</h3>
          <p className='text-center text-gray-600'>Procesando pago...</p>
        </div>
      )}

      {priorStepManualReviewNotice && paymentStep === 2 && (
        <div className='rounded-lg border border-amber-200 bg-amber-50 p-3'>
          <p className='text-sm text-amber-900'>
            <strong>Atencion:</strong> el pago del paso anterior quedo en revision. Completa la
            confirmacion; los addons se activaran cuando todo quede confirmado.
          </p>
        </div>
      )}

      <div className='grid grid-cols-1 gap-6 items-stretch lg:grid-cols-2'>
        <div className='h-full'>
          <div className='space-y-6'>
            {selectedAddonsSummary?.length > 0 && (
              <div className='flex h-full flex-col rounded-[24px] border border-slate-200 bg-white p-6 shadow-sm'>
                <div className='mb-4 flex items-center justify-between'>
                  <div>
                    <p className='text-[14px] font-sans font-semibold uppercase tracking-[0.18em] text-primary/80'>
                      Resumen
                    </p>
                    <h4 className='mt-2 text-xl font-sans font-bold text-slate-800'>
                      Add-ons seleccionados
                    </h4>
                  </div>
                  <span className='text-xs font-sans text-slate-400'>
                    {selectedAddonsSummary.length} items
                  </span>
                </div>

                <div className='divide-y divide-gray-100'>
                  {selectedAddonsSummary.map(item => (
                    <div
                      key={item.addonId}
                      className='flex items-center justify-between py-3'
                    >
                      <div className='min-w-0 flex flex-col'>
                        <span className='truncate text-sm font-sans font-medium text-gray-800'>
                          {item.name}
                        </span>
                      </div>

                      <div className='flex shrink-0 items-center gap-2'>
                        <div className='flex h-10 w-10 items-center justify-center rounded-full bg-accent2 text-sm font-sans font-semibold text-white'>
                          {item.quantity}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className='mt-5 rounded-[20px] border border-accent2 bg-accent2/5 px-4 py-4'>
                  <div className='flex justify-between gap-4'>
                    <span className='text-sm text-slate-600'>Total</span>
                    <div className='text-right'>
                      <p className='text-xl font-sans font-semibold text-slate-900'>
                        {(Number(totalUf) || 0).toFixed(2)} UF + IVA
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {paymentStep === 1 || selectedCard ? (
              <div className='rounded-[24px] border border-slate-200 bg-white p-6 shadow-sm'>
                <div className='flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between'>
                  <div>
                    <p className='text-[14px] font-sans font-semibold uppercase tracking-[0.16em] text-primary/80'>
                      {paymentStep === 1 ? 'Tarjeta para el cobro' : 'Tarjeta seleccionada'}
                    </p>
                    <p className='mt-2 text-sm font-sans leading-6 text-slate-600'>
                      {paymentStep === 1
                        ? 'Elige la tarjeta guardada con la que se procesaran estos add-ons.'
                        : 'Esta es la tarjeta que se usara para completar la autorizacion del cobro de los add-ons.'}
                    </p>
                  </div>

                </div>

                <div className={`mt-4 ${paymentStep === 2 ? 'pointer-events-none' : ''}`}>
                  {paymentStep === 1 ? (
                    cardsLoading ? (
                      <div className='flex items-center justify-center rounded-[24px] border border-slate-200 bg-white py-10 shadow-sm'>
                        <div className='h-12 w-12 animate-spin rounded-full border-b-4 border-t-4 border-accent1' />
                      </div>
                    ) : cards.length > 0 ? (
                      <PaymentCardSelector
                        cards={cards}
                        selectedCardId={selectedCardId}
                        onSelectCard={onSelectCard}
                        disabled={isPending}
                      />
                    ) : (
                      <div className='rounded-[24px] border border-dashed border-slate-300 bg-slate-50 px-6 py-10 text-center'>
                        <h5 className='text-lg font-sans font-bold text-slate-800'>
                          No tienes tarjetas guardadas
                        </h5>
                        <p className='mt-3 text-sm font-sans leading-6 text-slate-600'>
                          Agrega una tarjeta para continuar con la contratacion de add-ons.
                        </p>
                      </div>
                    )
                  ) : (
                    <PaymentCardSelector
                      cards={[selectedCard]}
                      selectedCardId={String(selectedCard.id)}
                    />
                  )}
                </div>
              </div>
            ) : (
              <div className='rounded-[24px] border border-amber-200 bg-amber-50 p-4 text-sm font-sans text-amber-800 shadow-sm'>
                No se encontro la tarjeta seleccionada. Vuelve al paso anterior y elige una tarjeta
                antes de confirmar.
              </div>
            )}
          </div>
        </div>

        <div className='h-full'>
          <div className='flex h-full flex-col space-y-4 rounded-2xl border border-gray-200 bg-white p-4 shadow-sm'>
            <div>
              <h3 className='text-base font-semibold text-gray-900'>Metodo de pago</h3>
              <p className='mt-1 text-sm text-gray-500'>
                Completa la validacion para finalizar la contratacion.
              </p>
            </div>

            {/* Tarjeta para el cobro oculta en la columna derecha por requerimiento. */}
            {errorMessage && (
              <div className='rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700'>
                {errorMessage}
              </div>
            )}

            {paymentStep === 2 && (
              <p className='text-xs text-gray-500'>
                Si el pago queda en revision, los addons no se activan al instante hasta que se
                confirme.
              </p>
            )}

            {selectedCard ? (
              isFinalConfirmationStep ? (
                <div className='space-y-4'>
                  <div>
                    <p className='text-[14px] font-sans font-semibold uppercase tracking-[0.18em] text-primary/80'>
                      Confirmacion final
                    </p>
                    <h4 className='mt-2 text-xl font-sans font-bold text-slate-800'>
                      El ingreso del CVV
                    </h4>
                    <p className='mt-2 text-sm font-sans leading-6 text-slate-600'>
                      Abrimos automaticamente la ventana de confirmacion para que completes el CVV y
                      autorices el cobro de los add-ons. Si la cerraste, puedes abrirla nuevamente
                      desde aqui.
                    </p>
                  </div>

                  <CustomButton
                    title='Abrir confirmacion final'
                    color='accent2'
                    className='rounded-full px-5 py-2'
                    onClick={() => setShowFinalConfirmationModal(true)}
                  />
                </div>
              ) : (
                paymentForm
              )
            ) : (
              <div className='rounded-lg border border-accent2 bg-accent2/5 p-3 text-sm '>
                Selecciona una tarjeta para habilitar la validacion del cobro.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default AddonConfirmContent
